import { useCallback, useContext, useMemo, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { Entity } from "@common/types/entity";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import {
    updateBillExtractionDetails,
    updateInvoiceExtractionDetails,
} from "@api/financialDocument.api";
import { useEntities, useEntitiesById } from "@hooks/useEntities";
import { AccrualImporterContext } from "./accrualImporter.context";
import {
    accrualDocumentBeingProcessed,
    getAllAccrualLineItemExtractions,
    mergeUpdatedAccrualExtraction,
    resolveAccrualRowExtraction,
} from "./accrualImporter.helpers";
import { AccrualDocumentImporterMode } from "./accrualDocumentImporter.lib";

interface UseAccrualImporterEntityEditorParams {
    financialDocument: FinancialDocumentExtended;
    documentRowId: string;
    extractionId?: string;
}

export function buildDocumentWithEntityId({
    financialDocument,
    entityId,
    mode,
    extractionId,
}: {
    financialDocument: FinancialDocumentExtended;
    entityId: number | null;
    mode: AccrualDocumentImporterMode;
    extractionId?: string;
}): FinancialDocumentExtended {
    const extraction = resolveAccrualRowExtraction(
        financialDocument,
        mode,
        extractionId,
    );

    if (!extraction) {
        return {
            ...financialDocument,
            entityId,
        };
    }

    const updatedExtraction = {
        ...extraction,
        entityId,
    };

    if (mode === "bills") {
        return {
            ...financialDocument,
            documentBillsExtraction: financialDocument.documentBillsExtraction
                ? {
                      ...financialDocument.documentBillsExtraction,
                      billExtractions: mergeUpdatedAccrualExtraction({
                          allExtractions: getAllAccrualLineItemExtractions(
                              financialDocument,
                              "bills",
                          ),
                          updatedExtraction,
                      }),
                  }
                : null,
        };
    }

    return {
        ...financialDocument,
        documentInvoicesExtraction: financialDocument.documentInvoicesExtraction
            ? {
                  ...financialDocument.documentInvoicesExtraction,
                  invoiceExtractions: mergeUpdatedAccrualExtraction({
                      allExtractions: getAllAccrualLineItemExtractions(
                          financialDocument,
                          "invoices",
                      ),
                      updatedExtraction,
                  }),
              }
            : null,
    };
}

export function useAccrualImporterEntityEditor({
    financialDocument,
    documentRowId,
    extractionId,
}: UseAccrualImporterEntityEditorParams) {
    const { mode, updateDocumentRow, revalidateDocument } = useContext(
        AccrualImporterContext,
    );
    const entities = useEntities();
    const entitiesById = useEntitiesById();
    const financialDocumentRef = useRef(financialDocument);
    financialDocumentRef.current = financialDocument;

    const extraction = resolveAccrualRowExtraction(
        financialDocument,
        mode,
        extractionId,
    );
    const selectedEntityId = extraction?.entityId ?? financialDocument.entityId;
    const selectedEntity = useMemo(
        () =>
            selectedEntityId != null
                ? entitiesById.get(selectedEntityId)
                : undefined,
        [selectedEntityId, entitiesById],
    );

    const isProcessing = accrualDocumentBeingProcessed(financialDocument);

    const { mutate: persistEntity, isPending } = useMutation({
        mutationFn: (nextEntityId: number | null) => {
            const currentDocument = financialDocumentRef.current;
            const currentExtraction = resolveAccrualRowExtraction(
                currentDocument,
                mode,
                extractionId,
            );
            if (!currentExtraction) {
                throw new Error("Missing accrual extraction");
            }

            if (mode === "bills") {
                return updateBillExtractionDetails({
                    financialDocumentId: currentDocument.id,
                    billExtractionId: currentExtraction.id,
                    entityId: nextEntityId,
                });
            }

            return updateInvoiceExtractionDetails({
                financialDocumentId: currentDocument.id,
                invoiceExtractionId: currentExtraction.id,
                entityId: nextEntityId,
            });
        },
        onMutate: (nextEntityId) => {
            const previousDocument = financialDocumentRef.current;

            updateDocumentRow(documentRowId, {
                financialDocument: buildDocumentWithEntityId({
                    financialDocument: previousDocument,
                    entityId: nextEntityId,
                    mode,
                    extractionId,
                }),
            });

            return previousDocument;
        },
        onSuccess: () => {
            revalidateDocument({
                financialDocumentId: financialDocumentRef.current.id,
            });
        },
        onError: (_error, _entityId, previousDocument) => {
            if (previousDocument) {
                updateDocumentRow(documentRowId, {
                    financialDocument: previousDocument,
                });
                revalidateDocument({
                    financialDocumentId: previousDocument.id,
                });
            }
        },
    });

    const onEntityChanged = useCallback(
        (entity: Entity | undefined) => {
            if (isProcessing || isPending) {
                return;
            }

            const entityId = entity?.id ?? null;
            if (entityId === selectedEntityId) {
                return;
            }

            persistEntity(entityId);
        },
        [isProcessing, isPending, persistEntity, selectedEntityId],
    );

    const disabledReason = useMemo(() => {
        if (isProcessing) {
            return "Document is still processing";
        }
        if (isPending) {
            return "Saving...";
        }
        return undefined;
    }, [isProcessing, isPending]);

    return {
        entities,
        selectedEntity,
        onEntityChanged,
        disabledReason,
    };
}
